// 📄 components/credenciales/ObtenerCredencial.tsx
// Pedir y ver una contraseña guardada (docs/plans/plan-revelado-credenciales.md, R3):
//   - quien la guardó la ve directo (queda como "autoaprobada");
//   - otro con el permiso "Obtener credenciales" la pide con un motivo y,
//     aprobada, tiene 15 minutos para verla una sola vez.
// Antes de mostrarla siempre se vuelve a pedir la contraseña de OneHub: el
// backend exige una sesión de menos de 5 minutos (auth_time). La contraseña
// vive solo en el estado de RevelarCredencialModal y se borra a los 60 s.
import React, { useEffect, useRef, useState } from 'react';
import { EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { KeyRound, Lock, RefreshCw, X, Copy, Check, Clock, Send, Eye } from 'lucide-react';
import { useAuth } from '../../context/AuthContext.tsx';
import {
  AccesoCredencial,
  CredencialRevelada,
  crearSolicitud,
  cancelarSolicitud,
  revelarConSolicitud,
  revelarPropia
} from '../../services/credencialAccesoService.ts';

const MOTIVO_MIN = 10;
const MOTIVO_MAX = 500;

const NOMBRES_SECRETO: Record<string, string> = {
  'contraseña': 'Contraseña',
  apiKey: 'API Key',
  oauthSecret: 'OAuth Secret'
};

const horaCorta = (iso: string | null) => (iso ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '');

function Ventana({ titulo, onClose, children }: { titulo: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <h3 className="text-base font-medium text-white truncate">{titulo}</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-white flex-shrink-0"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">{children}</div>
      </div>
    </div>
  );
}

/** Pide la contraseña de OneHub y renueva el token (auth_time nuevo) antes de `onListo`. */
export function ReautenticarModal({ onListo, onClose }: { onListo: () => Promise<void>; onClose: () => void }) {
  const { user } = useAuth();
  const [password, setPassword] = useState('');
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!user?.email) { setError('Esta cuenta no tiene email asociado.'); return; }
    setTrabajando(true);
    try {
      await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, password));
      await user.getIdToken(true);
      setPassword('');
      await onListo();
    } catch (err: any) {
      if (err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password') setError('La contraseña no es correcta.');
      else if (err.code === 'auth/too-many-requests') setError('Demasiados intentos. Espera unos minutos.');
      else setError(err.message || 'No se pudo verificar tu contraseña.');
    } finally {
      setTrabajando(false);
    }
  };

  return (
    <Ventana titulo="Confirma que eres tú" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-xs text-gray-400 flex items-start gap-2">
          <Lock className="w-4 h-4 text-blue-400 flex-shrink-0" />
          Para ver una credencial guardada, vuelve a ingresar tu contraseña de OneHub ({user?.email}). Quedará registrado en el historial.
        </p>
        {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-2.5 rounded-lg text-xs">{error}</div>}
        <input
          type="password"
          autoFocus
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Tu contraseña de OneHub"
          className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
        />
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5">Cancelar</button>
          <button
            type="submit"
            disabled={trabajando || !password}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 px-4 py-2 rounded-lg text-sm font-semibold text-[#fff]"
          >
            {trabajando ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
            Ver credencial
          </button>
        </div>
      </form>
    </Ventana>
  );
}

/** Muestra el valor con botón de copiar y se cierra sola (los segundos los define el backend). */
export function RevelarCredencialModal({ revelada, onClose }: { revelada: CredencialRevelada; onClose: () => void }) {
  const [restantes, setRestantes] = useState(revelada.ocultarEnSegundos);
  const [copiado, setCopiado] = useState<string | null>(null);
  const cerrar = useRef(onClose);
  cerrar.current = onClose;

  useEffect(() => {
    const t = setInterval(() => setRestantes((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => { if (restantes <= 0) cerrar.current(); }, [restantes]);

  const copiar = async (clave: string, valor: string) => {
    await navigator.clipboard.writeText(valor);
    setCopiado(clave);
    setTimeout(() => setCopiado(null), 2000);
  };

  const filas = [
    ...(revelada.usuario ? [{ clave: 'usuario', etiqueta: 'Usuario', valor: revelada.usuario }] : []),
    ...Object.entries(revelada.secretos).map(([clave, valor]) => ({ clave, etiqueta: NOMBRES_SECRETO[clave] || clave, valor }))
  ];

  return (
    <Ventana titulo={revelada.etiqueta} onClose={onClose}>
      <div className="space-y-2">
        {filas.map((f) => (
          <div key={f.clave} className="bg-black/30 border border-white/10 rounded-lg p-3">
            <p className="text-[11px] text-gray-500 mb-1">{f.etiqueta}</p>
            <div className="flex items-center justify-between gap-2">
              <code className="text-sm text-white font-mono break-all">{f.valor}</code>
              <button type="button" onClick={() => copiar(f.clave, f.valor)} title="Copiar" className="p-1.5 rounded hover:bg-white/10 text-gray-400 hover:text-white flex-shrink-0">
                {copiado === f.clave ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-amber-300/90 flex items-center gap-1.5">
        <Clock className="w-3.5 h-3.5" /> Se oculta en {Math.max(restantes, 0)} s. Quedó registrado en el historial; considera cambiarla después de usarla.
      </p>
      <div className="flex justify-end">
        <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-300 hover:text-white bg-white/5 hover:bg-white/10">Cerrar ahora</button>
      </div>
    </Ventana>
  );
}

/**
 * Re-autenticación + revelado. `revelar` hace la llamada al backend; la
 * contraseña solo vive en el estado de esta pieza mientras se muestra.
 */
export function FlujoRevelar({ revelar, onClose, onRevelado }: { revelar: () => Promise<CredencialRevelada>; onClose: () => void; onRevelado?: () => void }) {
  const [revelada, setRevelada] = useState<CredencialRevelada | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (error) {
    return (
      <Ventana titulo="No se pudo ver la credencial" onClose={onClose}>
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>
        <div className="flex justify-end">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-300 bg-white/5 hover:bg-white/10">Cerrar</button>
        </div>
      </Ventana>
    );
  }
  if (revelada) {
    return <RevelarCredencialModal revelada={revelada} onClose={() => { setRevelada(null); onClose(); }} />;
  }
  return (
    <ReautenticarModal
      onClose={onClose}
      onListo={async () => {
        try {
          setRevelada(await revelar());
          onRevelado?.();
        } catch (err: any) {
          setError(err.message || 'No se pudo leer la credencial.');
        }
      }}
    />
  );
}

/** Pide la credencial: motivo obligatorio (lo ven los aprobadores y el historial). */
function PedirCredencialModal({ acceso, onEnviada, onClose }: { acceso: AccesoCredencial; onEnviada: () => void; onClose: () => void }) {
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const largo = motivo.trim().length;

  const enviar = async () => {
    setEnviando(true);
    setError(null);
    try {
      await crearSolicitud(acceso.credencial, motivo.trim());
      onEnviada();
    } catch (err: any) {
      setError(err.message || 'No se pudo enviar la solicitud.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Ventana titulo={`Pedir ${acceso.etiqueta}`} onClose={onClose}>
      <p className="text-xs text-gray-400">
        Tu solicitud llega a los aprobadores por correo. Cuando la aprueben tendrás 15 minutos para verla, una sola vez.
      </p>
      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-2.5 rounded-lg text-xs">{error}</div>}
      <div>
        <label className="block text-xs font-medium text-gray-400 mb-1">Motivo</label>
        <textarea
          autoFocus
          value={motivo}
          maxLength={MOTIVO_MAX}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="Ej: probar la conexión de CIG en QAS para el incidente 123"
          className="w-full h-24 resize-y bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
        />
        <p className="text-[11px] text-gray-500 mt-1">{largo < MOTIVO_MIN ? `Mínimo ${MOTIVO_MIN} caracteres.` : `${largo}/${MOTIVO_MAX}`}</p>
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5">Cancelar</button>
        <button
          type="button"
          onClick={enviar}
          disabled={enviando || largo < MOTIVO_MIN}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 px-4 py-2 rounded-lg text-sm font-semibold text-[#fff]"
        >
          {enviando ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Enviar solicitud
        </button>
      </div>
    </Ventana>
  );
}

/** Botones de "obtener" según lo que el backend dice que el usuario puede hacer. */
export default function ObtenerCredencial({ acceso, onCambio }: { acceso: AccesoCredencial; onCambio: () => void }) {
  const [abierto, setAbierto] = useState<'pedir' | 'propia' | 'aprobada' | null>(null);
  const [cancelando, setCancelando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { yo } = acceso;
  const vigente = yo.solicitudVigente;

  if (!yo.puedeRevelarDirecto && !yo.puedeSolicitar) return null;

  const cancelar = async () => {
    if (!vigente) return;
    setCancelando(true);
    setError(null);
    try {
      await cancelarSolicitud(vigente.id);
      onCambio();
    } catch (err: any) {
      setError(err.message || 'No se pudo cancelar la solicitud.');
    } finally {
      setCancelando(false);
    }
  };

  const boton = 'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all';

  return (
    <div className="space-y-2">
      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-2.5 rounded-lg text-xs">{error}</div>}
      <div className="flex flex-wrap items-center gap-2">
        {yo.puedeRevelarDirecto && (
          <button type="button" onClick={() => setAbierto('propia')} className={`${boton} bg-blue-600 hover:bg-blue-500 text-[#fff]`}>
            <KeyRound className="w-3.5 h-3.5" /> Ver contraseña
          </button>
        )}

        {yo.puedeSolicitar && !vigente && (
          <button type="button" onClick={() => setAbierto('pedir')} className={`${boton} bg-blue-600 hover:bg-blue-500 text-[#fff]`}>
            <Send className="w-3.5 h-3.5" /> Pedir contraseña
          </button>
        )}

        {yo.puedeSolicitar && vigente?.estado === 'pendiente' && (
          <>
            <span className="text-xs text-amber-300/90 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Tu solicitud está esperando aprobación.</span>
            <button type="button" onClick={cancelar} disabled={cancelando} className={`${boton} text-gray-400 hover:text-white hover:bg-white/10`}>
              {cancelando ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />} Cancelar solicitud
            </button>
          </>
        )}

        {yo.puedeSolicitar && vigente?.estado === 'aprobada' && (
          <>
            <button type="button" onClick={() => setAbierto('aprobada')} className={`${boton} bg-emerald-600 hover:bg-emerald-500 text-[#fff]`}>
              <KeyRound className="w-3.5 h-3.5" /> Ver contraseña (aprobada)
            </button>
            <span className="text-xs text-gray-400">Disponible hasta las {horaCorta(vigente.expiraEn)}, una sola vez.</span>
          </>
        )}
      </div>

      {abierto === 'pedir' && <PedirCredencialModal acceso={acceso} onClose={() => setAbierto(null)} onEnviada={() => { setAbierto(null); onCambio(); }} />}
      {abierto === 'propia' && (
        <FlujoRevelar revelar={() => revelarPropia(acceso.credencial)} onClose={() => { setAbierto(null); onCambio(); }} />
      )}
      {abierto === 'aprobada' && vigente && (
        <FlujoRevelar revelar={() => revelarConSolicitud(vigente.id)} onClose={() => { setAbierto(null); onCambio(); }} />
      )}
    </div>
  );
}
